import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { useHistory } from 'react-router-dom';
import { GetWorkflowExecutionResponse } from '@amzn/snow-nxt-workflow-service-typescript-client';
import {
	Tabs,
	Box,
	SpaceBetween,
	Flashbar,
	FlashbarProps,
	Header,
	Spinner,
	ProgressBar,
	Button,
} from '@amzn/awsui-components-console';

import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { sortedTaskStatus, isUserPresentInBindle } from 'src/utils/CommonUtils';
import { ExecutionRawEntity } from 'src/pages/manageExecution/ManageExecutionUtils';
import { snowIssueTemplate } from 'src/constants/CommonConstants';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import { AuthContext } from 'src/providers/AuthContext';
import TaskExecutionSummary from 'src/components/findEntity/TaskExecutionSummary';
import { ListTaskExecutionsResponse } from 'src/@types/selfServeTypes/listTaskExecutions.interface';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import WorkflowExecutionCanvas from '../selfServeWorkflows/executionView/workflowExecutionCanvas';

import CycleDataTab from './CycleDataTab';
import WorkflowExecutionTable from './workflowExecutionTable';
import SIMComponent from './SIMComponent';
import {
	EXECUTED_TASK_STATUSES,
	WAITING_TASK_STATUSES,
	filterSIMTasks,
	createStatusClickHandler,
} from './findEntityUtils';

interface TaskExecution {
	status: string;
	entities: string[] | undefined;
	taskType: string;
	taskExecutionId: string;
	taskId: string;
	taskName: string;
	[key: string]: unknown;
}

interface CycleStatusProps {
	cycleId: string;
	workflowType: string;
	workflowDefinitionName: string;
	cycleData?: Record<string, unknown> | null;
}

export default function CycleExecutionStatus({ cycleId, workflowType, workflowDefinitionName, cycleData }: CycleStatusProps) {
	const [currentTab, setCurrentTab] = useState<string>('cycleData');
	const [hasExecutePermissions, setHasExecutePermissions] = useState<boolean>(false);
	const [statusFilter, setStatusFilter] = useState<string | null>(null);
	const history = useHistory();

	const { userName } = useContext(AuthContext);

	const handleGoToNodes = () => {
		const params = new URLSearchParams({ workflowType, cycleId });
		history.push(`/entitySearch?${params.toString()}`);
	};

	const taskExecutionDataURL = `/workflows/${workflowType}/executions/${cycleId}/tasks`;

	const { isLoading, data, error, fetchData } = useFetchForSDS<ListTaskExecutionsResponse>({
		path: taskExecutionDataURL,
		method: 'GET',
		url: SNOW_NXT_WORKFLOW_ENDPOINT,
	});

	const { data: singleExecutionData, isLoading: isExecutionDataLoading } = useFetchForSDS<GetWorkflowExecutionResponse>({
		path: `/workflows/${workflowType}/executions/${cycleId}`,
		method: 'GET',
		url: SNOW_NXT_WORKFLOW_ENDPOINT,
	});

	const effectiveCycleData = useMemo(() => {
		const exec = singleExecutionData?.workflowExecutionData;
		if (exec) {
			const toStr = (v: Date | string | undefined) =>
				!v ? undefined : (v instanceof Date ? v.toISOString() : String(v));
			return {
				entities: [{
					CycleId: exec.workflowExecutionId,
					WorkflowType: exec.workflowId,
					Status: exec.status,
					Entities: exec.entities,
					CreationTime: toStr(exec.startTime),
					ActualStartTime: toStr(exec.startTime),
					ActualCompletionTime: toStr(exec.completionTime),
					LastStatusUpdateTime: toStr(exec.lastUpdateTime),
					LastStatusUpdatedBy: exec.lastUpdatedBy,
					Owner: exec.startedBy,
				} as ExecutionRawEntity],
			};
		}
		if (cycleData) {
			return {
				entities: [{
					CycleId: cycleData.cycleId,
					WorkflowType: cycleData.workflowType,
					Status: cycleData.status,
					Entities: cycleData.entities,
					CreationTime: cycleData.creationTime,
					ActualCompletionTime: cycleData.actualCompletionTime,
					LastStatusUpdateTime: cycleData.lastStatusUpdateTime,
					LastStatusUpdatedBy: cycleData.lastStatusUpdatedBy,
					Owner: cycleData.owner,
				} as ExecutionRawEntity],
			};
		}
		return null;
	}, [singleExecutionData, cycleData]);

	const taskExecutionData = data?.taskExecutions as TaskExecution[] | undefined;

	useEffect(() => {
		const checkExecutePermissions = async (): Promise<void> => {
			try {
				await isUserPresentInBindle(
					workflowType,
					userName,
					OLAF_WORKFLOW_BINDLE_DATA.type,
					OLAF_WORKFLOW_BINDLE_DATA.action,
					OLAF_WORKFLOW_BINDLE_DATA.namespace,
					null
				);
				setHasExecutePermissions(true);
			} catch {
				setHasExecutePermissions(false);
			}
		};

		checkExecutePermissions();
	}, [workflowType, userName]);

	const flashBarItems = useMemo<FlashbarProps.MessageDefinition[]>(() => {
		if (error) {
			return [{
				header: `Failed to fetch task execution data for cycle ${cycleId}`,
				type: 'error',
				content: (
					<p>
						Please reach out to SNOW Team. Issue{' '}
						<a href={snowIssueTemplate} target="_blank" rel="noreferrer">
							link
						</a>
					</p>
				),
				dismissible: true,
			}];
		}

		if (taskExecutionData?.length === 0) {
			return [{
				header: 'No task execution data available',
				type: 'warning',
				content: 'The workflow execution has no tasks.',
				dismissible: true,
			}];
		}

		return [];
	}, [error, taskExecutionData, cycleId]);

	const filterItems = (items: TaskExecution[], filterType: string[]): TaskExecution[] => {
		return items.filter((item) => filterType.includes(item.status));
	};

	const handleStatusClick = createStatusClickHandler(
		setCurrentTab,
		setStatusFilter,
		statusFilter
	);

	const handleTabChange = (activeTabId: string) => {
		setCurrentTab(activeTabId);
		setStatusFilter(null);
	};

	const refreshView = useCallback(() => {
		fetchData();
	}, [fetchData]);

	const tabs = useMemo(() => {
		const cycleDataTab = {
			label: 'Execution Data',
			id: 'cycleData',
			content: (
				<CycleDataTab cycleId={cycleId} workflowType={workflowType} workflowDefinitionName={workflowDefinitionName} prefetchedData={effectiveCycleData} isLoading={isExecutionDataLoading && !effectiveCycleData} />
			),
		};

		if (!taskExecutionData || taskExecutionData.length === 0) {
			return [cycleDataTab];
		}

		const executedTasks = filterItems(taskExecutionData, EXECUTED_TASK_STATUSES as unknown as string[]);
		const waitingTasks = filterItems(taskExecutionData, WAITING_TASK_STATUSES as unknown as string[]);
		const simTasks = filterSIMTasks(taskExecutionData);

		return [
			cycleDataTab,
			{
				label: 'Executed Tasks',
				id: 'executedTasks',
				content: (
					<WorkflowExecutionTable
						key={`executed-tasks-${statusFilter || 'all'}`}
						taskExecutionData={executedTasks.sort((a, b) => {
							return -sortedTaskStatus(a.status, b.status);
						})}
						workflowId={workflowType}
						workflowExecutionId={cycleId}
						entityName=""
						show={true}
						refreshView={refreshView}
						setFlashBarItems={() => {}}
						hasExecutePermissions={hasExecutePermissions}
						statusFilter={statusFilter}
					/>
				),
			},
			{
				label: 'Graphical Task Execution View',
				id: 'executionView',
				content: (
					<ReactFlowProvider>
						<WorkflowExecutionCanvas
							workflowId={workflowType}
							workflowExecutionId={cycleId}
							listTaskExecutionsResponse={
								data || { taskExecutions: [], dependencies: [] }
							}
							nodeName=""
							hasExecutePermissions={hasExecutePermissions}
							setFlashBarItems={() => {}}
							refreshView={refreshView}
						/>
					</ReactFlowProvider>
				),
			},
			{
				label: 'Waiting Tasks',
				id: 'Waiting',
				content: (
					<WorkflowExecutionTable
						workflowId={workflowType}
						workflowExecutionId={cycleId}
						taskExecutionData={waitingTasks}
						show={true}
						entityName=""
						hasExecutePermissions={hasExecutePermissions}
						tableTitle="Waiting/Scheduled Tasks"
					/>
				),
			},
			{
				label: 'SIM / TT Tracking',
				id: 'simTracking',
				content: (
					<SIMComponent simTasks={simTasks as any} workflowName={workflowType} />
				),
			},
		];
	}, [taskExecutionData, statusFilter, workflowType, cycleId, hasExecutePermissions, data, effectiveCycleData, refreshView, isExecutionDataLoading, workflowDefinitionName]);

	const finishedTasks = taskExecutionData
		? filterItems(taskExecutionData, ['COMPLETED', 'SKIPPED', 'OVERRIDEN']).length
		: 0;
	const totalTasks = taskExecutionData ? taskExecutionData.length : 0;
	const progress = totalTasks > 0 ? Math.round((finishedTasks / totalTasks) * 100) : 0;

	return (
		<Box>
			<SpaceBetween size="s">
				<Header
					variant="h1"
					actions={
						<Button onClick={handleGoToNodes}>
							View Entities
						</Button>
					}
				>
					{workflowDefinitionName} - Execution {cycleId}
				</Header>
				{!isLoading && taskExecutionData && taskExecutionData.length > 0 && (
					<ProgressBar
						value={progress}
						variant="standalone"
						status={progress === 100 ? 'success' : 'in-progress'}
						additionalInfo={`${finishedTasks} of ${totalTasks} tasks completed`}
					/>
				)}
				<Flashbar items={flashBarItems} />
				{isLoading ? (
					<Box textAlign="center" padding="xxl">
						<Spinner size="large" />
					</Box>
				) : (
					<>
						{taskExecutionData && taskExecutionData.length > 0 && (
							<TaskExecutionSummary
								taskExecutionData={taskExecutionData}
								entityName=""
								onStatusClick={handleStatusClick}
							/>
						)}
						<Tabs
							tabs={tabs}
							variant="default"
							activeTabId={currentTab}
							onChange={({ detail }) => handleTabChange(detail.activeTabId)}
						/>
					</>
				)}
			</SpaceBetween>
		</Box>
	);
}
